<script lang="ts">
	import "bulma-slider/dist/css/bulma-slider.min.css";
	import "bulma-switch/dist/css/bulma-switch.min.css";

	import { fly } from "svelte/transition";

	import { onMount, onDestroy } from "svelte";
	import Fa from "svelte-fa";
	import { faInfoCircle, faTimes } from "@fortawesome/free-solid-svg-icons";

	import { dldl, goto, yyyy, zzzz } from "./store.svelte";
	import { addOnsData } from "./lib/addOnsData";

	let isYearly: boolean = $state(false);
	let ready: boolean = $state(false);
	let numSubstations: number = $state(100);
	let numTransformers: number = $state(50000);
	let numCustomers: number = $state(750000);
	let activePopover: string | null = $state(null);

	// Paid add-ons configuration with reactive enabled state - filter out items that should be omitted from pricing
	let addOns = $state(addOnsData.filter(addon => !addon.omitFromPricing).map(addon => ({ ...addon, enabled: false })));

	let price: string = $derived.by(() => {
		let total: number = 0;
		
		// Base pricing
		total += numSubstations * 10;  // $100 per Distribution Substation
		total += numTransformers * 0.1;  // $0.5 per Transformer
		total += numCustomers * 0.01;  // $0.025 per Customer
		
		// Add $0.025 per customer for each enabled extension
		const enabledAddOnsCount = addOns.filter(addOn => addOn.enabled).length;
		total += numCustomers * 0.025 * enabledAddOnsCount;
		
		if (isYearly) {
			total = (total * 10) / 12;
		}
		return new Intl.NumberFormat("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(total);
	});

	let toggleIsYearly = () => {
		isYearly = !isYearly;
	};

	let togglePopover = (addOnId: string) => {
		activePopover = activePopover === addOnId ? null : addOnId;
	};

	let handleClickOutside = (event: MouseEvent) => {
		const target = event.target as Element;
		if (activePopover && !target.closest('.add-on-item-compact')) {
			activePopover = null;
		}
	};
	onMount(() => {
		ready = true;
		document.addEventListener('click', handleClickOutside);
	});

	onDestroy(() => {
		yyyy.logo.time = 877.5;
		yyyy.logo.usage = "wait";
		document.removeEventListener('click', handleClickOutside);
	});
</script>

{#if ready}
	<!-- <svelte:window on:pushstate={routing} /> -->
	<div class="column is-two-thirds not-too-wide">
		<div class="card lvwr-card" class:zeroBG={!ready}>
			<div
				class="card-content lvwr-card-content"
				transition:fly|local={{ y: -200 }}
			>
				<h1 class="title thin lvwr-title" class:dim={!ready} class:bri={ready}>Costing</h1>
				<div class="content lvwr-content" class:dim={!ready} class:bri={ready}>
					<div class="pricing-controls-row">
						<div class="pricing-control-group">
							<span class="pricing-label-compact">Distribution Substations:</span>
							<div class="field has-addons">
								<p class="control">
									<button class="button is-small" onclick={() => numSubstations = Math.max(1, numSubstations - 1)}>-</button>
								</p>
								<p class="control">
									<input class="input is-small has-text-centered" type="number" bind:value={numSubstations} min="1" max="1000" />
								</p>
								<p class="control">
									<button class="button is-small" onclick={() => numSubstations = Math.min(1000, numSubstations + 1)}>+</button>
								</p>
							</div>
						</div>

						<div class="pricing-control-group">
							<span class="pricing-label-compact">Transformers:</span>
							<div class="field has-addons">
								<p class="control">
									<button class="button is-small" onclick={() => numTransformers = Math.max(1, numTransformers - 1)}>-</button>
								</p>
								<p class="control">
									<input class="input is-small has-text-centered" type="number" bind:value={numTransformers} min="1" max="10000" />
								</p>
								<p class="control">
									<button class="button is-small" onclick={() => numTransformers = Math.min(10000, numTransformers + 1)}>+</button>
								</p>
							</div>
						</div>

						<div class="pricing-control-group">
							<span class="pricing-label-compact">Customers:</span>
							<div class="field has-addons">
								<p class="control">
									<button class="button is-small" onclick={() => numCustomers = Math.max(1, numCustomers - 1)}>-</button>
								</p>
								<p class="control">
									<input class="input is-small has-text-centered" type="number" bind:value={numCustomers} min="1" max="100000" />
								</p>
								<p class="control">
									<button class="button is-small" onclick={() => numCustomers = Math.min(100000, numCustomers + 1)}>+</button>
								</p>
							</div>
						</div>
					</div>
					
					<div class="add-ons-section">
						<div class="add-ons-grid">
							{#each addOns as addOn}
								<div class="add-on-item-compact">
									<input
										id="addon-{addOn.id}"
										type="checkbox"
										name="addon-{addOn.id}"
										class="switch is-small"
										bind:checked={addOn.enabled}
									/>
									<label for="addon-{addOn.id}" class="add-on-label-compact">
										<strong>{addOn.name}</strong>
										<button 
											class="info-button"
											onclick={() => togglePopover(addOn.id)}
											type="button"
										>
											<Fa icon={faInfoCircle} size="sm" />
										</button>
									</label>
									{#if activePopover === addOn.id}
										<div class="popover" transition:fly={{ y: -10, duration: 200 }}>
											<div class="popover-content">
												<button 
													class="popover-close"
													onclick={() => togglePopover(addOn.id)}
													type="button"
												>
													<Fa icon={faTimes} size="sm" />
												</button>
												<strong>{addOn.title}</strong>
												<p>{addOn.description}</p>
											</div>
										</div>
									{/if}
								</div>
							{/each}
							<div class="add-on-item-compact billing-item">
								<input
									id="is-yearly"
									type="checkbox"
									name="is-yearly"
									class="switch is-small"
									checked={isYearly}
									onclick={toggleIsYearly}
								/>
								<label for="is-yearly" class="add-on-label-compact">
									<strong>Annual Commitment</strong>
								</label>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer
				class="card-footer"
				class:dim={!ready}
				class:bri={ready}
				transition:fly|local={{ y: 200 }}
			>
				<div class="card-footer-item sign-in-up-footer">
					<button
						onclick={() => {
							goto("/");
						}}
						class="button lvwr-toggle">Back</button
					>
					<button class="button is-ghost price-display">${price}/month</button>
				</div>
			</footer>
		</div>
	</div>
{/if}

<style>
	.lvwr-card-content {
		height: 410px;
		overflow-y: auto;
	}

	.card-content {
		height: 410px;
	}

	.button.is-ghost {
		background-color: transparent;
		border: none;
	}

	.field {
		margin-top: 1rem;
	}

	.pricing-controls-row {
		display: flex;
		gap: 1rem;
		margin-bottom: 0.5rem;
		flex-wrap: wrap;
		justify-content: space-between;
	}

	.pricing-control-group {
		display: flex;
		flex-direction: column;
		align-items: center;
		flex: 1;
		min-width: 120px;
	}

	.pricing-label-compact {
		font-weight: 600;
		margin-bottom: 0.1rem;
		font-size: 0.9rem;
		color: #333;
	}

	.pricing-control-group input {
		width: 60px;
		font-weight: 600;
		-moz-appearance: textfield !important;
		appearance: textfield !important;
	}

	.pricing-control-group input::-webkit-outer-spin-button,
	.pricing-control-group input::-webkit-inner-spin-button {
		-webkit-appearance: none !important;
		margin: 0 !important;
	}

	.pricing-control-group button {
		width: 35px;
	}

	/* Remove default number input styling */
	input[type="number"]::-webkit-outer-spin-button,
	input[type="number"]::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	input[type="number"] {
		-moz-appearance: textfield;
		appearance: none;
		-webkit-appearance: none;
	}

	.add-ons-section {
		margin: 0.25rem 0;
		padding: 0.75rem;
		background-color: rgba(255, 255, 255, 0.1);
		border-radius: 4px;
		border: 1px solid #e0e0e0;
	}

	.add-ons-section h3 {
		margin-bottom: 0.5rem;
		color: #ff3e00;
		font-weight: 600;
	}

	.add-ons-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		justify-content: space-between;
	}

	.add-on-item-compact {
		flex: 1;
		min-width: 180px;
		padding: 0.35rem;
		background-color: rgba(255, 255, 255, 0.05);
		border-radius: 4px;
		position: relative;
	}

	.add-on-label-compact {
		display: block;
		margin-left: 2rem;
		line-height: 1.2;
		position: relative;
		font-size: 0.85rem;
	}

	.billing-item {
		background-color: rgba(255, 255, 255, 0.1);
		border: 1px solid #dbdbdb;
	}


	.info-button {
		background: none;
		border: none;
		color: #3273dc;
		cursor: pointer;
		margin-left: 0.5rem;
		padding: 0.25rem;
		border-radius: 50%;
		transition: all 0.2s;
		vertical-align: baseline;
	}

	.info-button:hover {
		background-color: rgba(50, 115, 220, 0.1);
		color: #ff3e00;
	}

	.popover {
		position: fixed;
		/* top: 50%; */
		/* left: 50%; */
		/* transform: translate(-50%, -5%); */
		width: 300px;
		z-index: 1000;
		max-height: 200px;
		overflow-y: auto;
	}

	/* Mobile positioning fix */
	@media (max-width: 768px) {
		.popover {
			position: fixed;
			bottom: 20%;
			left: 50%;
			transform: translateX(-50%) translateY(-80%);
			width: calc(100% - 40px);
			max-width: 350px;
		}
	}

	.popover-content {
		background: white;
		border: 1px solid #ddd;
		border-radius: 6px;
		padding: 0.75rem;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		font-size: 0.9rem;
		line-height: 1.4;
	}

	.popover-content strong {
		color: #ff3e00;
		display: block;
		margin-bottom: 0.5rem;
	}

	.popover-content p {
		margin: 0;
		color: #555;
	}

	.popover-close {
		position: absolute;
		top: 0.5rem;
		right: 0.5rem;
		background: none;
		border: none;
		color: #999;
		cursor: pointer;
		padding: 0.25rem;
		border-radius: 50%;
		transition: all 0.2s;
		line-height: 1;
	}

	.popover-close:hover {
		background-color: rgba(0, 0, 0, 0.1);
		color: #ff3e00;
	}
</style>
